import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coffee, LogOut, Plus, Pencil, Trash2, X, Save, GripVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3003').replace(/\/$/, '');
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [token, setToken] = useState(localStorage.getItem('rott_admin_token') || '');
    const [loading, setLoading] = useState(true);

    // Login form
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');

    // Data
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');

    // Modals
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showProductModal, setShowProductModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);

    // Form states
    const [categoryForm, setCategoryForm] = useState({ name: '', order: 0 });
    const [productForm, setProductForm] = useState({
        category_id: '',
        name: '',
        price: '',
        is_available: true,
        order: 0
    });

    // Check auth on mount
    useEffect(() => {
        if (token) {
            verifyToken();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const verifyToken = async () => {
        try {
            const res = await fetch(`${API}/admin/me`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setIsAuthenticated(true);
                fetchData();
            } else {
                localStorage.removeItem('rott_admin_token');
                setToken('');
            }
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        try {
            const res = await fetch(`${API}/admin/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            const data = await res.json();
            if (res.ok) {
                localStorage.setItem('rott_admin_token', data.access_token);
                setToken(data.access_token);
                setIsAuthenticated(true);
                fetchData();
            } else {
                setLoginError('Kullanıcı adı veya şifre hatalı');
            }
        } catch (e) {
            console.error("Giriş Hatası Detayı:", e);
            setLoginError('Bağlantı hatası (Konsolu kontrol edin)');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('rott_admin_token');
        setToken('');
        setIsAuthenticated(false);
    };

    const fetchData = async () => {
        try {
            const [catRes, prodRes] = await Promise.all([
                fetch(`${API}/categories`),
                fetch(`${API}/products`)
            ]);
            setCategories(await catRes.json());
            setProducts(await prodRes.json());
        } catch (e) {
            console.error(e);
        }
    };

    const authHeaders = () => ({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    });

    // Category CRUD
    const openCategoryModal = (category = null) => {
        if (category) {
            setEditingCategory(category);
            setCategoryForm({ name: category.name, order: category.order });
        } else {
            setEditingCategory(null);
            setCategoryForm({ name: '', order: categories.length });
        }
        setShowCategoryModal(true);
    };

    const saveCategory = async () => {
        try {
            const url = editingCategory
                ? `${API}/categories/${editingCategory.id}`
                : `${API}/categories`;
            const method = editingCategory ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: authHeaders(),
                body: JSON.stringify(categoryForm)
            });

            if (res.ok) {
                fetchData();
                setShowCategoryModal(false);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const deleteCategory = async (id) => {
        if (!window.confirm('Bu kategoriyi ve içindeki tüm ürünleri silmek istediğinize emin misiniz?')) return;
        try {
            await fetch(`${API}/categories/${id}`, {
                method: 'DELETE',
                headers: authHeaders()
            });
            fetchData();
        } catch (e) {
            console.error(e);
        }
    };

    // Product CRUD
    const openProductModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setProductForm({
                category_id: product.category_id,
                name: product.name,
                price: product.price.toString(),
                is_available: product.is_available,
                order: product.order
            });
        } else {
            setEditingProduct(null);
            setProductForm({
                category_id: categories[0]?.id || '',
                name: '',
                price: '',
                is_available: true,
                order: products.length
            });
        }
        setShowProductModal(true);
    };

    const saveProduct = async () => {
        try {
            const url = editingProduct
                ? `${API}/products/${editingProduct.id}`
                : `${API}/products`;
            const method = editingProduct ? 'PUT' : 'POST';

            const payload = {
                ...productForm,
                price: parseFloat(productForm.price) || 0
            };

            const res = await fetch(url, {
                method,
                headers: authHeaders(),
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                fetchData();
                setShowProductModal(false);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const deleteProduct = async (id) => {
        if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
        try {
            await fetch(`${API}/products/${id}`, {
                method: 'DELETE',
                headers: authHeaders()
            });
            fetchData();
        } catch (e) {
            console.error(e);
        }
    };


    const getCategoryName = (id) => {
        return categories.find(c => c.id === id)?.name || 'Bilinmiyor';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0C0A09] flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-[#D4A373] border-t-transparent rounded-full" />
            </div>
        );
    }

    // Login Screen
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-[#0C0A09] flex items-center justify-center p-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md"
                >
                    <div className="text-center mb-8">
                        <Coffee className="w-12 h-12 text-[#D4A373] mx-auto mb-4" />
                        <h1 className="font-heading font-bold text-3xl text-[#F5F5F4]">Yönetim Paneli</h1>
                        <p className="text-[#A8A29E] mt-2">Rott Coffee Co</p>
                    </div>

                    <form onSubmit={handleLogin} className="glass-card p-8 rounded-2xl space-y-6">
                        {loginError && (
                            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">
                                {loginError}
                            </div>
                        )}

                        <div>
                            <label className="block text-[#A8A29E] text-sm mb-2">Kullanıcı Adı</label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="w-full bg-[#1C1917] border border-[#292524] text-[#F5F5F4] px-4 py-3 rounded-xl focus:outline-none focus:border-[#D4A373] transition-colors"
                                placeholder="Kullanıcı adı"
                            />
                        </div>

                        <div>
                            <label className="block text-[#A8A29E] text-sm mb-2">Şifre</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-[#1C1917] border border-[#292524] text-[#F5F5F4] px-4 py-3 rounded-xl focus:outline-none focus:border-[#D4A373] transition-colors"
                                placeholder="Şifre"
                            />
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-[#D4A373] text-[#0C0A09] py-3 rounded-xl font-semibold hover:bg-[#E5B88F] transition-colors"
                        >
                            Giriş Yap
                        </button>
                    </form>

                    <p className="text-center text-[#57534E] text-sm mt-6">
                        <a href="/" className="hover:text-[#D4A373] transition-colors">← Ana Sayfaya Dön</a>
                    </p>
                </motion.div>
            </div>
        );
    }

    // Admin Dashboard
    return (
        <div className="min-h-screen bg-[#0C0A09]">
            {/* Header */}
            <header className="bg-[#1C1917] border-b border-[#292524] sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Coffee className="w-8 h-8 text-[#D4A373]" />
                        <span className="font-heading font-bold text-xl text-[#F5F5F4]">Admin Panel</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <a href="/menu" target="_blank" className="text-[#A8A29E] hover:text-[#D4A373] text-sm transition-colors">
                            Menüyü Görüntüle →
                        </a>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 text-[#A8A29E] hover:text-red-400 transition-colors"
                        >
                            <LogOut className="w-5 h-5" />
                            <span className="hidden sm:inline">Çıkış</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Tabs */}
            <div className="max-w-7xl mx-auto px-6 py-6 border-b border-[#292524] flex gap-6 overflow-x-auto">
                <button
                    onClick={() => setActiveTab('overview')}
                    className={`pb-4 px-2 font-medium whitespace-nowrap transition-colors ${activeTab === 'overview'
                        ? 'text-[#D4A373] border-b-2 border-[#D4A373]'
                        : 'text-[#A8A29E] hover:text-[#F5F5F4]'
                        }`}
                >
                    Sistem Özeti
                </button>
                <button
                    onClick={() => setActiveTab('products')}
                    className={`pb-4 px-2 font-medium whitespace-nowrap transition-colors ${activeTab === 'products'
                        ? 'text-[#D4A373] border-b-2 border-[#D4A373]'
                        : 'text-[#A8A29E] hover:text-[#F5F5F4]'
                        }`}
                >
                    Ürünler ({products.length})
                </button>
                <button
                    onClick={() => setActiveTab('categories')}
                    className={`pb-4 px-2 font-medium whitespace-nowrap transition-colors ${activeTab === 'categories'
                        ? 'text-[#D4A373] border-b-2 border-[#D4A373]'
                        : 'text-[#A8A29E] hover:text-[#F5F5F4]'
                        }`}
                >
                    Kategoriler ({categories.length})
                </button>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        <h2 className="font-heading font-bold text-3xl text-[#F5F5F4]">Sistem Özeti</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="glass-card p-6 rounded-2xl border border-[#292524] border-l-4 border-l-[#D4A373]">
                                <p className="text-[#A8A29E] text-sm mb-1">Toplam Ürün</p>
                                <div className="text-4xl font-bold text-[#F5F5F4]">{products.length}</div>
                            </div>
                            <div className="glass-card p-6 rounded-2xl border border-[#292524] border-l-4 border-l-[#D4A373]">
                                <p className="text-[#A8A29E] text-sm mb-1">Toplam Kategori</p>
                                <div className="text-4xl font-bold text-[#F5F5F4]">{categories.length}</div>
                            </div>
                            <div className="glass-card p-6 rounded-2xl border border-[#292524] border-l-4 border-l-[#D4A373]">
                                <p className="text-[#A8A29E] text-sm mb-1">Aktif Ürünler</p>
                                <div className="text-4xl font-bold text-[#F5F5F4]">{products.filter(p => p.is_available).length}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Products Tab */}
                {activeTab === 'products' && (
                    <div>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                            <h2 className="font-heading font-bold text-3xl text-[#F5F5F4]">Ürün Yönetimi</h2>
                            <button
                                onClick={() => openProductModal()}
                                className="flex items-center justify-center gap-2 bg-[#D4A373] text-[#0C0A09] px-6 py-3 rounded-xl font-medium hover:bg-[#E5B88F] transition-colors"
                                disabled={categories.length === 0}
                            >
                                <Plus className="w-5 h-5" />
                                Yeni Ürün
                            </button>
                        </div>

                        {categories.length === 0 ? (
                            <div className="glass-card p-12 rounded-2xl border border-[#292524] text-center">
                                <Coffee className="w-12 h-12 text-[#57534E] mx-auto mb-4" />
                                <p className="text-[#A8A29E] text-lg">Önce bir kategori oluşturmalısınız.</p>
                            </div>
                        ) : products.length === 0 ? (
                            <div className="glass-card p-12 rounded-2xl border border-[#292524] text-center">
                                <Coffee className="w-12 h-12 text-[#57534E] mx-auto mb-4" />
                                <p className="text-[#A8A29E] text-lg">Henüz ürün eklenmemiş.</p>
                            </div>
                        ) : (
                            <div className="grid gap-4">
                                {products.map((product) => (
                                    <div
                                        key={product.id}
                                        className="glass-card p-4 rounded-xl border border-[#292524] flex items-center gap-4 hover:border-[#D4A373]/30 transition-colors"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-[#F5F5F4] font-semibold text-lg truncate">{product.name}</h3>
                                            <p className="text-[#A8A29E] text-sm">{getCategoryName(product.category_id)}</p>
                                        </div>
                                        <div className="text-[#D4A373] font-bold text-xl whitespace-nowrap px-4">
                                            ₺{product.price.toFixed(2)}
                                        </div>
                                        <div className={`hidden md:block px-3 py-1 rounded-full text-xs font-medium ${product.is_available ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                                            {product.is_available ? 'Aktif' : 'Pasif'}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => openProductModal(product)}
                                                className="p-3 bg-[#1C1917] rounded-lg text-[#A8A29E] hover:text-[#D4A373] hover:bg-[#292524] transition-colors"
                                            >
                                                <Pencil className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => deleteProduct(product.id)}
                                                className="p-3 bg-[#1C1917] rounded-lg text-[#A8A29E] hover:text-red-400 hover:bg-[#292524] transition-colors"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Categories Tab */}
                {activeTab === 'categories' && (
                    <div>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                            <h2 className="font-heading font-bold text-3xl text-[#F5F5F4]">Kategori Yönetimi</h2>
                            <button
                                onClick={() => openCategoryModal()}
                                className="flex items-center justify-center gap-2 bg-[#D4A373] text-[#0C0A09] px-6 py-3 rounded-xl font-medium hover:bg-[#E5B88F] transition-colors"
                            >
                                <Plus className="w-5 h-5" />
                                Yeni Kategori
                            </button>
                        </div>

                        {categories.length === 0 ? (
                            <div className="glass-card p-12 rounded-2xl border border-[#292524] text-center">
                                <GripVertical className="w-12 h-12 text-[#57534E] mx-auto mb-4" />
                                <p className="text-[#A8A29E] text-lg">Henüz kategori eklenmemiş.</p>
                            </div>
                        ) : (
                            <div className="grid gap-4">
                                {categories.map((category) => (
                                    <div
                                        key={category.id}
                                        className="glass-card p-4 rounded-xl border border-[#292524] flex items-center gap-4 hover:border-[#D4A373]/30 transition-colors"
                                    >
                                        <div className="p-3 bg-[#1C1917] rounded-lg text-[#57534E]">
                                            <GripVertical className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-[#F5F5F4] font-semibold text-lg">{category.name}</h3>
                                            <p className="text-[#A8A29E] text-sm">
                                                {products.filter(p => p.category_id === category.id).length} ürün
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => openCategoryModal(category)}
                                                className="p-3 bg-[#1C1917] rounded-lg text-[#A8A29E] hover:text-[#D4A373] hover:bg-[#292524] transition-colors"
                                            >
                                                <Pencil className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => deleteCategory(category.id)}
                                                className="p-3 bg-[#1C1917] rounded-lg text-[#A8A29E] hover:text-red-400 hover:bg-[#292524] transition-colors"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Category Modal */}
            <AnimatePresence>
                {showCategoryModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 z-50"
                        onClick={() => setShowCategoryModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="glass-card bg-[#1C1917] p-8 rounded-3xl border border-[#292524] w-full max-w-md shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-8">
                                <h3 className="font-heading font-bold text-2xl text-[#F5F5F4]">
                                    {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
                                </h3>
                                <button onClick={() => setShowCategoryModal(false)} className="p-2 bg-[#292524] hover:bg-[#3f3936] rounded-full text-[#A8A29E] transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-5">
                                <div>
                                    <label className="block text-[#A8A29E] text-sm mb-2 ml-1">Kategori Adı</label>
                                    <input
                                        type="text"
                                        value={categoryForm.name}
                                        onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                                        className="w-full bg-[#0C0A09] border border-[#292524] text-[#F5F5F4] px-4 py-3 rounded-xl focus:outline-none focus:border-[#D4A373] transition-colors"
                                        placeholder="Örn: Espresso Bazlı"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[#A8A29E] text-sm mb-2 ml-1">Sıra Numarası (Opsiyonel)</label>
                                    <input
                                        type="number"
                                        value={categoryForm.order}
                                        onChange={(e) => setCategoryForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                                        className="w-full bg-[#0C0A09] border border-[#292524] text-[#F5F5F4] px-4 py-3 rounded-xl focus:outline-none focus:border-[#D4A373] transition-colors"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-4 mt-8">
                                <button
                                    onClick={() => setShowCategoryModal(false)}
                                    className="flex-1 px-4 py-3 border border-[#292524] text-[#A8A29E] rounded-xl hover:bg-[#292524] font-medium transition-colors"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={saveCategory}
                                    className="flex-1 px-4 py-3 bg-[#D4A373] text-[#0C0A09] rounded-xl font-semibold hover:bg-[#E5B88F] transition-colors flex items-center justify-center gap-2"
                                >
                                    <Save className="w-5 h-5" />
                                    Kaydet
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Product Modal */}
            <AnimatePresence>
                {showProductModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 z-50 overflow-y-auto"
                        onClick={() => setShowProductModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="glass-card bg-[#1C1917] p-8 rounded-3xl border border-[#292524] w-full max-w-lg shadow-2xl my-8 relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-8">
                                <h3 className="font-heading font-bold text-2xl text-[#F5F5F4]">
                                    {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün'}
                                </h3>
                                <button onClick={() => setShowProductModal(false)} className="p-2 bg-[#292524] hover:bg-[#3f3936] rounded-full text-[#A8A29E] transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-4 custom-scrollbar">
                                <div>
                                    <label className="block text-[#A8A29E] text-sm mb-2 ml-1">Kategori</label>
                                    <select
                                        value={productForm.category_id}
                                        onChange={(e) => setProductForm(prev => ({ ...prev, category_id: e.target.value }))}
                                        className="w-full bg-[#0C0A09] border border-[#292524] text-[#F5F5F4] px-4 py-3 rounded-xl focus:outline-none focus:border-[#D4A373] transition-colors appearance-none"
                                    >
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[#A8A29E] text-sm mb-2 ml-1">Ürün Adı</label>
                                    <input
                                        type="text"
                                        value={productForm.name}
                                        onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                                        className="w-full bg-[#0C0A09] border border-[#292524] text-[#F5F5F4] px-4 py-3 rounded-xl focus:outline-none focus:border-[#D4A373] transition-colors"
                                        placeholder="Örn: Flat White"
                                    />
                                </div>


                                <div>
                                    <label className="block text-[#A8A29E] text-sm mb-2 ml-1">Fiyat (₺)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={productForm.price}
                                        onChange={(e) => setProductForm(prev => ({ ...prev, price: e.target.value }))}
                                        className="w-full bg-[#0C0A09] border border-[#292524] text-[#F5F5F4] px-4 py-3 rounded-xl focus:outline-none focus:border-[#D4A373] transition-colors"
                                        placeholder="0.00"
                                    />
                                </div>


                                <div className="flex items-center gap-3 p-4 bg-[#0C0A09] border border-[#292524] rounded-xl">
                                    <input
                                        type="checkbox"
                                        id="is_available"
                                        checked={productForm.is_available}
                                        onChange={(e) => setProductForm(prev => ({ ...prev, is_available: e.target.checked }))}
                                        className="w-5 h-5 rounded bg-[#1C1917] border-[#292524] text-[#D4A373] focus:ring-[#D4A373]"
                                    />
                                    <label htmlFor="is_available" className="text-[#F5F5F4] font-medium cursor-pointer flex-1">
                                        Bu ürünü menüde göster
                                    </label>
                                </div>
                            </div>

                            <div className="flex gap-4 mt-8 pt-4 border-t border-[#292524]">
                                <button
                                    onClick={() => setShowProductModal(false)}
                                    className="flex-1 px-4 py-3 border border-[#292524] text-[#A8A29E] rounded-xl hover:bg-[#292524] font-medium transition-colors"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={saveProduct}
                                    className="flex-1 px-4 py-3 bg-[#D4A373] text-[#0C0A09] rounded-xl font-semibold hover:bg-[#E5B88F] transition-colors flex items-center justify-center gap-2"
                                >
                                    <Save className="w-5 h-5" />
                                    Kaydet
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminPage;
